import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import * as scrapersApi from "@api/scrapers.api";
import type { Scraper, ScraperStats } from "@/types";

interface ScrapersState {
  list: Scraper[];
  stats: ScraperStats | null;
  loading: boolean;
}

const initialState: ScrapersState = {
  list: [],
  stats: null,
  loading: false,
};

export const fetchScrapers = createAsyncThunk("scrapers/fetchAll", async () => {
  return await scrapersApi.getAll();
});

export const fetchStats = createAsyncThunk("scrapers/fetchStats", async () => {
  return await scrapersApi.getStats();
});

export const createScraper = createAsyncThunk(
  "scrapers/create",
  async (data: Partial<Scraper>) => {
    return await scrapersApi.create(data);
  },
);

export const updateScraper = createAsyncThunk(
  "scrapers/update",
  async ({ id, data }: { id: string; data: Partial<Scraper> }) => {
    return await scrapersApi.update(id, data);
  },
);

export const deleteScraper = createAsyncThunk(
  "scrapers/delete",
  async (id: string) => {
    await scrapersApi.remove(id);
    return id;
  },
);

const scrapersSlice = createSlice({
  name: "scrapers",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchScrapers.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchScrapers.fulfilled, (state, action) => {
        state.list = action.payload;
        state.loading = false;
      })
      .addCase(fetchScrapers.rejected, (state) => {
        state.loading = false;
      })
      .addCase(fetchStats.fulfilled, (state, action) => {
        state.stats = action.payload;
      })
      .addCase(createScraper.fulfilled, (state, action) => {
        state.list.unshift(action.payload);
      })
      .addCase(updateScraper.fulfilled, (state, action) => {
        const idx = state.list.findIndex((s) => s.id === action.payload.id);
        if (idx !== -1) state.list[idx] = action.payload;
      })
      .addCase(deleteScraper.fulfilled, (state, action) => {
        state.list = state.list.filter((s) => s.id !== action.payload);
      });
  },
});

export default scrapersSlice.reducer;
